import React from "react";
import { Dialog, Button, Typography, Box } from "@mui/material";
import { FiX, FiAlertTriangle } from "react-icons/fi";
import { colors } from "../theme/modernTheme";

/**
 * Standard delete confirmation dialog for the entire project
 *
 * @param {boolean} open - Dialog open state
 * @param {Function} onClose - Handler to close dialog
 * @param {Function} onConfirm - Handler for delete confirmation
 * @param {string} title - Custom title
 * @param {string} message - Custom message
 * @param {number} count - Number of items (for bulk delete)
 * @param {string} itemName - Name of item(s)
 * @param {boolean} deleting - Loading state
 * @param {string} maxWidth - Custom max width for the dialog
 */
export default function DeleteConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  count,
  itemName = "item",
  deleting = false,
  children,
  maxWidth = "400px",
}) {
  const displayTitle =
    title ||
    (count && count > 1
      ? `Delete ${count} ${itemName}?`
      : `Delete ${itemName}?`);
  const displayMessage =
    message ||
    (children
      ? null
      : "This action cannot be undone. Are you sure you want to proceed?");

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "4px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          maxWidth: maxWidth,
          m: 2,
        },
      }}>
      <Box sx={{ position: "absolute", right: 8, top: 8 }}>
        {!deleting && (
          <Button
            onClick={onClose}
            sx={{
              minWidth: 0,
              p: 0.5,
              color: colors.text.secondary,
              "&:hover": {
                color: colors.text.primary,
                backgroundColor: "transparent",
              },
            }}>
            <FiX size={18} />
          </Button>
        )}
      </Box>

      <Box sx={{ p: 3, textAlign: "center" }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            backgroundColor: colors.error.main + "10",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}>
          <FiAlertTriangle size={22} color={colors.error.main} />
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: colors.text.primary,
            fontSize: "1.1rem",
            mb: displayMessage || children ? 1 : 0,
          }}>
          {displayTitle}
        </Typography>

        {displayMessage && (
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, px: 2, mb: 3 }}>
            {displayMessage}
          </Typography>
        )}

        {children && <Box sx={{ mb: 3 }}>{children}</Box>}

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            disabled={deleting}
            sx={{
              py: 1,
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: 600,
              borderColor: colors.border.main,
              color: colors.text.secondary,
              "&:hover": {
                borderColor: colors.text.primary,
                backgroundColor: colors.background.light,
              },
            }}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            fullWidth
            autoFocus
            disabled={deleting}
            sx={{
              py: 1,
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: colors.error.main,
              "&:hover": {
                backgroundColor: colors.error.dark,
              },
            }}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
